import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { SearchIcon, WrenchIcon } from "lucide-react";
import SelectEquipmentModal from "./components/SelectEquipmentModal";
import { useAvailableCompanyBranches } from "@/features/companyBranches/hooks/useAvailableCompanyBranches";
import { useEquipments } from "@/features/equipments/hooks/useEquipments";
import { EquipmentsTransformer } from "@/features/equipments/services/equipmentTransformer";
import { useMaintenanceWorkflows } from "../Create/hooks/useMaintenanceWorkflows";
import { MaintenanceWorkflowsTransformer } from "../../services/maintenanceWorkflowsTransformer";
import { Option } from "@/common/types/option";
import { maintenanceWorkflowsTableColumns } from "../../constants/maintenanceWorkflowsTableColumns.constant";
import ListHeader from "@/common/components/ListHeader";
import { SheetFilters } from "@/common/components/SheetFilters";
import ActualEntryCascadeFilter from "./components/ActualEntryCascadeFilter";
import { useMaintenanceWorkflowsPossibleStatuses } from "../../hooks/useMaintenanceWorkflowsPossibleStatuses";
import { useMaintenanceForwardingsPossibleStatuses } from "../../hooks/useMaintenanceForwardingsPossibleStatuses";
import { useMaintenanceForwardingsActualEntryFilterOptions } from "../../hooks/useMaintenanceForwardingsActualEntryFilterOptions";
import { MaintenanceWorkflowFiltersTransformer } from "../../services/maintenanceWorkflowFiltersTransformer";

const MaintenanceWorkflowsList = () => {
  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search);
  const [searchEquipment, setSearchEquipment] = useDebounce('');
  const [searchBranch, setSearchBranch] = useDebounce('');

  const { data: workflowStatusOptions } = useMaintenanceWorkflowsPossibleStatuses({
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowFiltersTransformer.statusOptionsTransformer(),
    }),
  });
  const { data: forwardingStatusOptions } = useMaintenanceForwardingsPossibleStatuses({
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowFiltersTransformer.statusOptionsTransformer(),
    }),
  });
  const { data: actualEntryFilters } = useMaintenanceForwardingsActualEntryFilterOptions({
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowFiltersTransformer.actualEntryFiltersTransformer(),
    }),
  });

  const actualEntryTree = actualEntryFilters?.tree ?? [];
  const entryTypeFilters = actualEntryFilters?.entryTypeFilters ?? [];

  const {
    data: companyBranchOptions,
  } = useAvailableCompanyBranches({
    filters: {
      search: searchBranch
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const {
    data: equipmentsOptions,
  } = useEquipments({
    filters: {
      search: searchEquipment,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EquipmentsTransformer.optionsTransformer(),
    }),
  });

  const {
    data: workflows,
    meta: workflowsMeta,
    isPending: workflowsIsPending,
    refetch: refetchWorkflows,
    error: workflowsError = {} as any,
  } = useMaintenanceWorkflows({
    filters: {
      search,
      page: Number(filterParams.page) || 1,
      branchId: filterParams.branch?.value,
      equipmentId: filterParams.equipment?.value,
      status: filterParams.workflowStatus?.value,
      currentForwardingStatus: filterParams.currentForwardingStatus?.value,
      checkinEntryType: filterParams.checkinEntryType?.value,
      establishmentChoiceEntryType: filterParams.establishmentChoiceEntryType?.value,
      financialReviewEntryType: filterParams.financialReviewEntryType?.value,
      directiveEntryType: filterParams.directiveEntryType?.value,
      fiscalAssessmentEntryType: filterParams.fiscalAssessmentEntryType?.value,
      checkoutEntryType: filterParams.checkoutEntryType?.value,
      actualEntry: filterParams.actualEntry?.value,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: MaintenanceWorkflowsTransformer.tableTransformer(),
    }),
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<WrenchIcon className="w-10 h-10" strokeWidth={1} />}
        title="Processos de manutenção"
        actions={(
          <SelectEquipmentModal />
        )}
      />

      <ErrorMessageWrapper
        className=""
        statusCode={workflowsError?.status}
        refetchFunction={refetchWorkflows}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              name="search"
              LeftIcon={<SearchIcon />}
              placeholder="Pesquise pela descrição"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                setSearch((e.target as HTMLInputElement).value);
                setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
              }}
              value={filterParams.search}
            />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <ActualEntryCascadeFilter
                  tree={actualEntryTree}
                  value={filterParams.actualEntry}
                  onChange={(actualEntry) => setFilterParams((prev) => ({ ...prev, actualEntry }))}
                />

                <ListFilters
                  filters={[
                    {
                      className: '',
                      dataTestid: 'workflow_status',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, workflowStatus: e })),
                      value: filterParams.workflowStatus,
                      options: [{ label: 'Todos', value: '' }, ...(workflowStatusOptions || [])],
                      placeholder: 'Status do processo',
                    },
                    {
                      className: '',
                      dataTestid: 'current_forwarding_status',
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, currentForwardingStatus: e })),
                      value: filterParams.currentForwardingStatus,
                      options: [{ label: 'Todos', value: '' }, ...(forwardingStatusOptions || [])],
                      placeholder: 'Status do Encaminhamento',
                    },
                    {
                      className: '',
                      dataTestid: 'equipment',
                      onInputChange: setSearchEquipment,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, equipment: e })),
                      value: filterParams.equipment,
                      options: [{ label: 'Todas', value: '' }, ...(equipmentsOptions as Option[] || [])],
                      placeholder: 'Equipamento',
                    },
                    {
                      className: '',
                      dataTestid: 'branch',
                      onInputChange: setSearchBranch,
                      onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                      value: filterParams.branch,
                      options: [{ label: 'Todas', value: '' }, ...(companyBranchOptions || [])],
                      placeholder: 'Filial',
                    },
                  ]}
                />

                <SheetFilters
                  filters={entryTypeFilters.map((filter) => ({
                    label: filter.label,
                    dataTestid: filter.filterKey,
                    options: [
                      {
                        label: 'Todas',
                        value: '',
                        dataTestid: `${filter.filterKey}_option_all`,
                        selected: !filterParams[filter.filterKey]?.value,
                        onClick: () => setFilterParams((prev) => ({ ...prev, [filter.filterKey]: { label: '', value: '' } })),
                      },
                      ...filter.options.map((option) => ({
                        label: option.label,
                        value: option.value,
                        dataTestid: `${filter.filterKey}_option_${option.value}`,
                        selected: filterParams[filter.filterKey]?.value === option.value,
                        onClick: () => setFilterParams((prev) => ({ ...prev, [filter.filterKey]: option })),
                      })),
                    ],
                  }))}
                />
              </div>
            </div>
          </div>

          <Table
            className="max-h-[calc(100vh-250px)]"
            columns={maintenanceWorkflowsTableColumns}
            isLoadingData={workflowsIsPending}
            page={filterParams.page || 1}
            rows={workflows}
            totalPages={workflowsMeta.total_pages}
            totalCount={workflowsMeta.total}
            hasPagination
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default MaintenanceWorkflowsList;
